import React, { useState, useRef } from "react";
import { Button } from "../../../../../../ui/components/Button";
import { FeatherUpload, FeatherCheckCircle, FeatherXCircle, FeatherLoader } from "@subframe/core";
import { ExcelData, ExcelColumn } from "../types";
import { PeriodLeadModal } from "./PeriodLeadModal";
import { uploadExcelFile } from "../../../../data/api/pivotApi";

// Helper to get period/lead labels (matching PeriodLeadModal)
const getPeriodLabel = (value: string): string => {
  // Expected: "dec-24" (lowercase month abbreviation + "-" + 2-digit year)
  if (!value) return value;
  const [m, yy] = value.split("-");
  if (!m || !yy) return value;

  const monthMap: Record<string, string> = {
    jan: "January",
    feb: "February",
    mar: "March",
    apr: "April",
    may: "May",
    jun: "June",
    jul: "July",
    aug: "August",
    sep: "September",
    oct: "October",
    nov: "November",
    dec: "December",
  };

  const monthName = monthMap[m.toLowerCase()];
  const year = parseInt(yy, 10);
  const fullYear = Number.isFinite(year) ? (year <= 50 ? 2000 + year : 1900 + year) : yy;

  return monthName ? `${monthName} ${fullYear}` : value;
};

const getLeadLabel = (value: string): string => {
  // Lead is now stored as a full path string like: "BS > Assets > Current Assets"
  return value;
};

interface Step1DataSourceProps {
  onDataSelected: (data: ExcelData & { fileId?: string; columns?: ExcelColumn[] }, period: string, lead: string, leadLevelId: number | null) => void;
  onNext: () => void;
}


export const Step1DataSource: React.FC<Step1DataSourceProps> = ({
  onDataSelected,
  onNext,
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string>("");
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");
  const [selectedLead, setSelectedLead] = useState<string>("");
  const [selectedLeadLevelId, setSelectedLeadLevelId] = useState<number | null>(null);
  const [showPeriodLeadModal, setShowPeriodLeadModal] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!extension || !["xlsx", "xls"].includes(extension)) {
      setUploadError("Invalid file type. Only .xlsx and .xls files are allowed.");
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError("File size exceeds maximum limit of 10MB.");
      return;
    }

    setUploadedFile(file);
    setUploadError("");
    // Open modal when file is uploaded
    setShowPeriodLeadModal(true);
  };

  const handlePeriodLeadConfirm = (period: string, lead: string, leadLevelId: number | null) => {
    setSelectedPeriod(period);
    setSelectedLead(lead);
    setSelectedLeadLevelId(leadLevelId);
    setUploadError("");
  };

  const handleNext = async () => {
    // Validate data source
    if (!uploadedFile) {
      setUploadError("Please upload a file.");
      return;
    }

    // Validate period and lead
    if (!selectedPeriod || !selectedLead) {
      setUploadError("Please select period and lead.");
      setShowPeriodLeadModal(true);
      return;
    }

    setUploading(true);
    setUploadError("");

    try {
      // Upload and parse Excel file
      const result = await uploadExcelFile(uploadedFile, selectedPeriod, selectedLead, selectedLeadLevelId);

      // Convert to ExcelData format with additional metadata
      const excelData: ExcelData & { fileId: string; columns: ExcelColumn[] } = {
        headers: result.headers,
        rows: result.sampleRows || [],
        fileId: result.fileId,
        columns: result.columns,
      };

      onDataSelected(excelData, selectedPeriod, selectedLead, selectedLeadLevelId);
      onNext();
    } catch (err: any) {
      console.error("Upload error:", err);
      setUploadError(err.message || "Failed to upload file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h2 className="text-heading-2 font-heading-2 text-default-font mb-2">
          Select Data Source
        </h2>
        <p className="text-body font-body text-subtext-color">
          Upload a new Excel file or select from existing data sources
        </p>
      </div>

      {/* Upload New File Section */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <div className="w-1 h-6 bg-brand-600 rounded"></div>
          <h3 className="text-heading-3 font-heading-3 text-default-font">
            Upload New File
          </h3>
        </div>

        <div className="flex items-center gap-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button
            variant="brand-primary"
            icon={uploading ? <FeatherLoader className="animate-spin" /> : <FeatherUpload />}
            onClick={handleUploadClick}
            disabled={uploading}
          >
            {uploading ? "Uploading..." : "Choose Excel File"}
          </Button>
          {uploadedFile && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-brand-50 border border-brand-200">
              <FeatherCheckCircle className="text-brand-600" />
              <span className="text-body font-body text-default-font">
                {uploadedFile.name}
              </span>
              <span className="text-caption font-caption text-subtext-color">
                ({(uploadedFile.size / 1024).toFixed(2)} KB)
              </span>
            </div>
          )}
        </div>
      </div>


      {/* Show selected Period & Lead if already selected */}
      {uploadedFile && (selectedPeriod || selectedLead) && (
        <div className="mt-4 pt-6 border-t border-neutral-border">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-6 bg-brand-600 rounded"></div>
            <h3 className="text-heading-3 font-heading-3 text-default-font">
              Selected Period & Lead
            </h3>
          </div>
          <div className="flex items-center gap-4">
            {selectedPeriod && (
              <div className="px-3 py-2 rounded-md bg-brand-50 border border-brand-200">
                <span className="text-caption font-caption text-subtext-color">Period: </span>
                <span className="text-body font-body text-default-font">
                  {getPeriodLabel(selectedPeriod)}
                </span>
              </div>
            )}
            {selectedLead && (
              <div className="px-3 py-2 rounded-md bg-brand-50 border border-brand-200">
                <span className="text-caption font-caption text-subtext-color">Lead: </span>
                <span className="text-body font-body text-default-font">
                  {getLeadLabel(selectedLead)}
                </span>
              </div>
            )}
            <Button
              variant="neutral-tertiary"
              size="small"
              onClick={() => setShowPeriodLeadModal(true)}
            >
              Change
            </Button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {uploadError && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-error-50 border border-error-200">
          <FeatherXCircle className="text-error-600" />
          <span className="text-body font-body text-error-700">{uploadError}</span>
        </div>
      )}

      {/* Next Button */}
      <div className="flex justify-end">
        <Button
          variant="brand-primary"
          onClick={handleNext}
          disabled={!uploadedFile || !selectedPeriod || !selectedLead || uploading}
          icon={uploading ? <FeatherLoader className="animate-spin" /> : undefined}
        >
          {uploading ? "Processing..." : "Next: Configure Pivot"}
        </Button>
      </div>

      {/* Period & Lead Modal */}
      <PeriodLeadModal
        isOpen={showPeriodLeadModal}
        onClose={() => setShowPeriodLeadModal(false)}
        onConfirm={handlePeriodLeadConfirm}
        initialPeriod={selectedPeriod}
        initialLead={selectedLead}
      />
    </div>
  );
};
